import { memo, useState } from 'react';
import type { Notification } from '@echo-portal/shared';
import { BellIcon } from '@/components/icons';
import { NotificationBadge } from './NotificationBadge';

interface NotificationItemProps {
  notification: Notification;
  /** True when the row was unread at popover open and the user clicked
   *  it (or used "mark all as read") during this session. Drives the
   *  soft `var(--gray-3)` trail tint. Resets when the popover closes
   *  (NotificationList clears its `recentlyReadIds` set on unmount). */
  recentlyRead?: boolean;
  /** True when this is the last row in the list — drops the row's
   *  bottom border so the footer divider doesn't double up. */
  isLast?: boolean;
  onMarkRead: (id: string) => void;
  onNavigate: (notification: Notification) => void;
}

/**
 * Single row in the Activity Center popover (and the full
 * Notifications page).
 *
 * Layout: 32-px round actor avatar with a coloured status badge in
 * the lower-right, a text block (title + 2-line clamped message +
 * timestamp), and a right-edge red dot when unread. Click marks read
 * (if unread) and navigates to the resource.
 */
export const NotificationItem = memo(function NotificationItem({
  notification,
  recentlyRead = false,
  isLast = false,
  onMarkRead,
  onNavigate,
}: NotificationItemProps) {
  const [hovered, setHovered] = useState(false);
  const formattedDate = new Date(notification.createdAt).toLocaleDateString(
    'en-US',
    { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
  );

  const hasUrl = isNavigable(notification);

  const handleClick = () => {
    if (!notification.isRead) onMarkRead(notification.id);
    onNavigate(notification);
  };

  // Hover wins over the recently-read trail (so a row the user is
  // about to interact with feels live). Both stay distinguishable
  // from the default transparent background.
  const background = hovered
    ? 'var(--gray-2)'
    : recentlyRead
      ? 'var(--gray-3)'
      : 'transparent';

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="button"
      tabIndex={0}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 16px',
        cursor: hasUrl ? 'pointer' : 'default',
        backgroundColor: background,
        borderBottom: isLast ? 'none' : '1px solid var(--gray-4)',
        transition: 'background-color 120ms ease',
      }}
    >
      <NotificationAvatar notification={notification} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          className="text-sm"
          style={{
            margin: 0,
            fontWeight: 600,
            color: 'var(--gray-12)',
            lineHeight: 1.3,
          }}
        >
          {notification.title}
        </p>
        <p
          // 2-line clamp keeps row height stable when the body wraps
          // (typical for "X has changes requested: …" with a quoted
          // resource name).
          className="text-sm"
          style={{
            margin: '2px 0 0',
            color: 'var(--gray-11)',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {notification.message}
        </p>
        <p
          className="text-xs"
          style={{
            margin: '4px 0 0',
            color: 'var(--gray-9)',
          }}
        >
          {formattedDate}
        </p>
      </div>

      {/* Right-edge unread marker. Vertically anchored to the title
          line so it doesn't drift when the message wraps. */}
      {!notification.isRead && (
        <span
          aria-label="Unread"
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'var(--red-9)',
            flexShrink: 0,
            marginTop: 6,
          }}
        />
      )}
    </div>
  );
});

/**
 * Rounded-square actor avatar with the status-icon badge in the
 * corner. The 8 px corner radius matches the header avatar in
 * `UserAvatarMenu` so the two main avatar surfaces in the app share
 * one shape, and the 44 px footprint matches the header avatar's
 * size too. Three fallbacks, in order:
 *   1. `actorAvatarUrl` → real photo
 *   2. `actorName` → initial in a tinted rounded-square
 *   3. system notification → generic bell glyph
 */
function NotificationAvatar({ notification }: { notification: Notification }) {
  const initial = notification.actorName?.charAt(0).toUpperCase() ?? null;

  return (
    <div
      style={{
        position: 'relative',
        width: 44,
        height: 44,
        flexShrink: 0,
      }}
    >
      {notification.actorAvatarUrl ? (
        <img
          src={notification.actorAvatarUrl}
          alt={notification.actorName ?? ''}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 8,
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <div
          aria-hidden="true"
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 8,
            background: 'var(--gray-4)',
            color: 'var(--gray-11)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 17,
            fontWeight: 600,
          }}
        >
          {initial ?? <BellIcon width={18} height={18} />}
        </div>
      )}

      <span
        style={{
          position: 'absolute',
          right: -2,
          bottom: -2,
        }}
      >
        <NotificationBadge type={notification.type} size={16} />
      </span>
    </div>
  );
}

function isNavigable(notification: Notification): boolean {
  const { resourceType, resourceId } = notification;
  if (!resourceId) return false;
  return resourceType === 'branch' || resourceType === 'review';
}

export default NotificationItem;
